import { useEffect, useState } from "react";
import CoachLayout from "@/components/CoachLayout";
import api from "@/lib/api";
import { Gift, Loader2, Save, Play, Square, Users, History, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function CoachWelcomeOffer() {
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState(false);
  const [normalWeeks, setNormalWeeks] = useState(1);
  const [notifyDays, setNotifyDays] = useState(2);
  const [wpc, setWpc] = useState(4);
  const [campQuota, setCampQuota] = useState(10);
  const [campCycles, setCampCycles] = useState(1);
  const [newQuota, setNewQuota] = useState("");

  const load = () => api.get("/admin/welcome-offer").then((r) => {
    setV(r.data);
    setNormalWeeks(r.data.normal_free_weeks);
    setNotifyDays(r.data.notify_days_before_end);
    setWpc(r.data.weeks_per_cycle);
    setCampCycles(r.data.campaign?.free_cycles || 1);
    setNewQuota(r.data.campaign?.quota || 10);
  }).catch(() => toast.error("Chargement impossible"));
  useEffect(() => { load(); }, []);

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const setMode = (mode) => run(() => api.post("/admin/welcome-offer/mode", { mode }), `Mode ${mode === "campaign" ? "campagne" : "normal"} activé`);
  const saveNormal = () => run(() => api.post("/admin/welcome-offer/normal-weeks", { weeks: Number(normalWeeks) }), "Durée offerte enregistrée");
  const saveNotify = () => run(() => api.post("/admin/welcome-offer/notify-days", { days: Number(notifyDays) }), "Préavis enregistré");
  const saveWpc = () => run(() => api.post("/admin/program-config", { weeks_per_cycle: Number(wpc) }), "Longueur de cycle enregistrée");

  const startCampaign = () => {
    if (!window.confirm(`Démarrer une campagne : quota ${campQuota} bénéficiaire(s), ${campCycles} cycle(s) offert(s) ?`)) return;
    run(() => api.post("/admin/welcome-offer/campaign/start", { quota: Number(campQuota), free_cycles: Number(campCycles) }), "Campagne démarrée");
  };
  const stopCampaign = () => {
    if (!window.confirm("Arrêter la campagne ? Les accès déjà accordés sont conservés. Retour au mode normal.")) return;
    run(() => api.post("/admin/welcome-offer/campaign/stop"), "Campagne arrêtée");
  };
  const updateQuota = () => {
    if (!window.confirm(`Modifier le quota à ${newQuota} ? (impossible sous le nombre déjà attribué)`)) return;
    run(() => api.post("/admin/welcome-offer/campaign/quota", { quota: Number(newQuota) }), "Quota mis à jour");
  };

  if (!v) return <CoachLayout><div className="pulse-organic w-3 h-3 rounded-full bg-sage mx-auto mt-20" /></CoachLayout>;
  const c = v.campaign || {};
  const isCampaign = v.mode === "campaign";

  const numInput = (val, set, tid, min = 1) => (
    <input data-testid={tid} type="number" min={min} value={val} onChange={(e) => set(e.target.value)}
      className="w-24 border border-border rounded-sm px-2 py-1.5 text-sm tabnum" />
  );

  return (
    <CoachLayout>
      <div className="fade-up max-w-3xl" data-testid="welcome-offer-admin">
        <h1 className="text-2xl font-medium flex items-center gap-2"><Gift size={22} strokeWidth={1.6} /> Offre de bienvenue</h1>
        <p className="mt-1 text-sm text-obsidian/50">Pilote l'accès gratuit accordé aux nouveaux coureurs. Aucune durée ni quota n'est figé : tout se règle ici.</p>

        {/* MODE */}
        <div className="mt-6 border border-border rounded-xl bg-white p-4">
          <div className="text-sm font-semibold text-obsidian/80">Mode actif</div>
          <div className="mt-3 flex gap-2">
            <button data-testid="wo-mode-normal" onClick={() => setMode("normal")} disabled={busy}
              className={`rounded-full px-4 py-2 text-sm border transition-colors ${!isCampaign ? "bg-obsidian text-white border-obsidian" : "border-border text-obsidian/70 hover:bg-sage/10"}`}>Normal — 1re période offerte</button>
            <button data-testid="wo-mode-campaign" onClick={() => setMode("campaign")} disabled={busy}
              className={`rounded-full px-4 py-2 text-sm border transition-colors ${isCampaign ? "bg-obsidian text-white border-obsidian" : "border-border text-obsidian/70 hover:bg-sage/10"}`}>Campagne — 1er cycle offert</button>
          </div>
          <div className="mt-2 text-xs text-obsidian/50" data-testid="wo-mode-status">
            Statut : <span className="font-semibold">{isCampaign ? (c.active ? "Campagne en cours" : "Campagne inactive") : "Mode normal"}</span>
          </div>
        </div>

        {/* NORMAL */}
        <div className="mt-4 border border-border rounded-xl bg-white p-4">
          <div className="text-sm font-semibold text-obsidian/80">Mode normal</div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-sm text-obsidian/60">Durée offerte :</span>
            {numInput(normalWeeks, setNormalWeeks, "wo-normal-weeks")}
            <span className="text-sm text-obsidian/60">semaine(s)</span>
            <button data-testid="wo-normal-save" onClick={saveNormal} disabled={busy}
              className="rounded-sm bg-obsidian text-white px-3 py-1.5 text-sm inline-flex items-center gap-1.5"><Save size={13} /> Enregistrer</button>
          </div>
        </div>

        {/* CAMPAGNE */}
        <div className="mt-4 border border-border rounded-xl bg-white p-4" data-testid="wo-campaign-block">
          <div className="text-sm font-semibold text-obsidian/80">Campagne</div>
          {!c.active ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-sm text-obsidian/60">Quota :</span>
              {numInput(campQuota, setCampQuota, "wo-campaign-quota")}
              <span className="text-sm text-obsidian/60">Cycles offerts :</span>
              {numInput(campCycles, setCampCycles, "wo-campaign-cycles")}
              <button data-testid="wo-campaign-start" onClick={startCampaign} disabled={busy}
                className="rounded-sm bg-sage text-white px-4 py-1.5 text-sm inline-flex items-center gap-1.5"><Play size={13} /> Démarrer la campagne</button>
            </div>
          ) : (
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg bg-sage/5 border border-border p-3" data-testid="wo-stat-quota"><div className="text-2xl font-light tabnum">{c.quota}</div><div className="text-xs text-obsidian/50">Quota</div></div>
                <div className="rounded-lg bg-sage/5 border border-border p-3" data-testid="wo-stat-granted"><div className="text-2xl font-light tabnum">{c.granted}</div><div className="text-xs text-obsidian/50">Attribuées</div></div>
                <div className="rounded-lg bg-sage/5 border border-border p-3" data-testid="wo-stat-remaining"><div className="text-2xl font-light tabnum">{c.remaining}</div><div className="text-xs text-obsidian/50">Restantes</div></div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-obsidian/60">Nouveau quota :</span>
                {numInput(newQuota, setNewQuota, "wo-new-quota")}
                <button data-testid="wo-quota-update" onClick={updateQuota} disabled={busy}
                  className="rounded-sm bg-obsidian text-white px-3 py-1.5 text-sm inline-flex items-center gap-1.5"><Save size={13} /> Mettre à jour</button>
                <button data-testid="wo-campaign-stop" onClick={stopCampaign} disabled={busy}
                  className="rounded-sm border border-destructive/40 text-destructive px-3 py-1.5 text-sm inline-flex items-center gap-1.5 hover:bg-destructive/5"><Square size={13} /> Arrêter</button>
              </div>
              <p className="text-xs text-obsidian/45 flex items-center gap-1.5"><AlertTriangle size={13} /> Le quota ne peut pas descendre sous le nombre déjà attribué. À quota atteint, retour automatique au mode normal.</p>
            </div>
          )}
        </div>

        {/* PARAMÈTRES */}
        <div className="mt-4 border border-border rounded-xl bg-white p-4">
          <div className="text-sm font-semibold text-obsidian/80">Paramètres</div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-sm text-obsidian/60">Préavis de fin d'accès :</span>
            {numInput(notifyDays, setNotifyDays, "wo-notify-days", 0)}
            <span className="text-sm text-obsidian/60">jour(s) avant</span>
            <button data-testid="wo-notify-save" onClick={saveNotify} disabled={busy}
              className="rounded-sm bg-obsidian text-white px-3 py-1.5 text-sm inline-flex items-center gap-1.5"><Save size={13} /> Enregistrer</button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-sm text-obsidian/60">Longueur d'un cycle :</span>
            {numInput(wpc, setWpc, "wo-wpc")}
            <span className="text-sm text-obsidian/60">semaines (mode campagne = 1 cycle offert)</span>
            <button data-testid="wo-wpc-save" onClick={saveWpc} disabled={busy}
              className="rounded-sm bg-obsidian text-white px-3 py-1.5 text-sm inline-flex items-center gap-1.5"><Save size={13} /> Enregistrer</button>
          </div>
        </div>

        {/* BÉNÉFICIAIRES */}
        <div className="mt-4 border border-border rounded-xl bg-white p-4">
          <div className="text-sm font-semibold text-obsidian/80 flex items-center gap-2"><Users size={15} /> Bénéficiaires ({v.grants_total})</div>
          <div className="mt-3 max-h-64 overflow-y-auto" data-testid="wo-beneficiaries">
            {(v.beneficiaries || []).length === 0 ? <p className="text-sm text-obsidian/40">Aucun bénéficiaire pour l'instant.</p> : (
              <table className="w-full text-sm">
                <thead><tr className="text-[11px] uppercase tracking-wide text-obsidian/45 text-left"><th className="pb-2">Email</th><th className="pb-2">Mode</th><th className="pb-2">Attribué le</th></tr></thead>
                <tbody>
                  {v.beneficiaries.map((b, i) => (
                    <tr key={i} className="border-t border-border" data-testid={`wo-benef-${i}`}>
                      <td className="py-1.5">{b.email_norm || "—"}</td>
                      <td className="py-1.5">{b.mode}</td>
                      <td className="py-1.5 text-obsidian/50">{(b.granted_at || "").slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* HISTORIQUE */}
        <div className="mt-4 border border-border rounded-xl bg-white p-4">
          <div className="text-sm font-semibold text-obsidian/80 flex items-center gap-2"><History size={15} /> Historique des changements</div>
          <div className="mt-3 max-h-56 overflow-y-auto space-y-1.5" data-testid="wo-audit">
            {(v.audit || []).length === 0 ? <p className="text-sm text-obsidian/40">Aucun changement enregistré.</p> :
              v.audit.map((a, i) => (
                <div key={i} className="text-xs text-obsidian/60 flex items-center gap-2" data-testid={`wo-audit-${i}`}>
                  <span className="text-obsidian/40">{(a.at || "").slice(0, 16).replace("T", " ")}</span>
                  <span className="font-semibold">{a.action}</span>
                </div>
              ))}
          </div>
        </div>
      </div>
    </CoachLayout>
  );
}
